/**
 * Phase 5 batch 2 Slice 6 — append-only audit log Firestore writer.
 *
 * Wires the form-engine's `subscribeAudit(listener)` (Phase 1 Slice 8)
 * to a Firestore subcollection at
 *
 *   applications/{applicationId}/audit/{eventId}
 *
 * Each `AuditEvent` becomes a single document. Event IDs come from
 * Firestore's auto-id (`addDoc`), so the writer never collides on
 * concurrent writes from multiple tabs.
 *
 * ## Redaction is upstream
 *
 * Per ADR 0013, the form-engine emitter has *already* redacted PII
 * payloads (`oldValue` / `newValue`) before they reach this writer
 * (see `packages/form-engine/src/audit/redaction.ts`). This module
 * therefore **does not re-redact**. It serializes whatever the
 * emitter hands it. If a future change weakens upstream redaction
 * the leak surfaces here, but layering redactions would mask the
 * regression.
 *
 * ## Append-only semantics
 *
 * The Firestore rules enforce append-only at the database boundary:
 * - `create` is allowed for the application's owner.
 * - `update`, `delete` are flatly denied.
 *
 * The writer relies on those rules for security; on the client side
 * it simply uses `addDoc` (never `setDoc` on an existing path,
 * never `deleteDoc`). A buggy or malicious caller cannot rewrite
 * history through this adapter.
 *
 * ## Back-pressure / batching
 *
 * Hydration of an existing snapshot fires one audit event per
 * answer. For a fully-filled I-485 (~580 fields) that's ~580 events
 * in a tight loop on app boot. Without batching, each event is a
 * round-trip to Firestore; the boot path would be both slow and
 * over-quota.
 *
 * The writer therefore buffers events and flushes via
 * `writeBatch(firestore).commit()` when EITHER:
 *
 * 1. The buffer reaches `BATCH_FLUSH_THRESHOLD` (500 — Firestore's
 *    documented per-batch maximum).
 * 2. `BATCH_DEBOUNCE_MS` (200ms) has elapsed since the last event.
 *
 * Listener errors don't propagate (the form-engine emitter
 * already isolates listener exceptions on a microtask), but the
 * writer surfaces flush errors to an optional `onError` hook so the
 * application can show a "your audit trail couldn't be saved" toast.
 *
 * Source-cited Firebase APIs (verified against
 * `node_modules/@firebase/firestore/dist/index.d.ts`):
 *   - `collection(parent, path)` → CollectionReference
 *   - `doc(collection)` → DocumentReference (auto-id)
 *   - `writeBatch(firestore)` → WriteBatch (max 500 ops)
 *   - `WriteBatch.set(ref, data)` → WriteBatch
 *   - `WriteBatch.commit()` → Promise<void>
 *
 * https://firebase.google.com/docs/firestore/manage-data/transactions#batched-writes
 */

import type { AuditEvent } from '@inside-us-aos/form-engine';
import {
  type Firestore,
  collection,
  doc,
  writeBatch,
} from 'firebase/firestore';

/** Firestore's per-batch write cap (documented). */
export const BATCH_FLUSH_THRESHOLD = 500;
/** Quiescence timeout that triggers a flush even if the batch isn't full. */
export const BATCH_DEBOUNCE_MS = 200;

/** Shape persisted under `applications/{appId}/audit/{eventId}`. */
export interface PersistedAuditEvent {
  /** Stable event type — currently always 'answer_changed'. */
  type: 'answer_changed';
  /** Wall-clock millis on the writing client (engine-supplied). */
  timestamp: number;
  /** Field whose answer changed. */
  fieldId: string;
  /**
   * Repeatable group instance, when applicable. Stored when present
   * in the source event; omitted for singletons (Firestore rejects
   * `undefined` values, so we strip rather than write `undefined`).
   */
  instanceKey?: string;
  /**
   * The redacted-or-raw old value, exactly as the upstream emitter
   * emitted it. The shape is `AnswerValue | { type: 'redacted', reason: 'pii' }`.
   */
  oldValue?: unknown;
  /** As above for the new value. */
  newValue?: unknown;
  /**
   * Auth uid of the actor at write-time. Required by the Firestore
   * rule (the rule asserts `actorUserId == request.auth.uid`).
   */
  actorUserId: string;
  /**
   * ISO-8601 mirror of `timestamp`. Convenient for queries / human
   * scanning in the Firebase Console; the rule does not require it.
   */
  timestampIso: string;
}

/** Engine subset the writer consumes. Decoupled from `FormEngine` so
 *  tests can pass a minimal stub. */
export interface AuditSource {
  subscribeAudit: (listener: (event: AuditEvent) => void) => () => void;
}

export interface AuditWriterOptions {
  /**
   * Auth uid of the current user. Stamped onto every event so the
   * Firestore rule (`actorUserId == request.auth.uid`) accepts the
   * write. The applicant-web binds this to the live auth state.
   */
  actorUserId: string;
  /** Optional hook for surfacing flush errors. Defaults to a no-op. */
  onError?: (err: unknown) => void;
  /** Test seam — override the debounce timer. */
  debounceMs?: number;
}

export interface AuditConnection {
  /** Detaches the writer from the source and flushes any buffered events. */
  disconnect: () => Promise<void>;
  /** Forces an immediate flush. Useful before sign-out / tab close. */
  flush: () => Promise<void>;
  /** Number of events currently buffered (test introspection). */
  bufferedCount: () => number;
}

export interface FirestoreAuditWriter {
  /** Subscribes the writer to `engine.subscribeAudit(...)` and
   *  returns a connection handle. */
  connect: (source: AuditSource, applicationId: string) => AuditConnection;
}

/**
 * Builds an append-only audit-log writer against the supplied
 * `Firestore`. The writer does not own SDK initialization.
 */
export const createFirestoreAuditWriter = (
  firestore: Firestore,
  options: AuditWriterOptions,
): FirestoreAuditWriter => {
  const debounceMs = options.debounceMs ?? BATCH_DEBOUNCE_MS;
  const onError = options.onError ?? ((): void => {});
  const actorUserId = options.actorUserId;

  const connect = (
    source: AuditSource,
    applicationId: string,
  ): AuditConnection => {
    const auditCol = collection(
      firestore,
      'applications',
      applicationId,
      'audit',
    );

    let buffer: PersistedAuditEvent[] = [];
    let timer: ReturnType<typeof setTimeout> | null = null;
    let pendingFlush: Promise<void> = Promise.resolve();
    let disposed = false;

    const flushNow = async (): Promise<void> => {
      if (timer !== null) {
        clearTimeout(timer);
        timer = null;
      }
      if (buffer.length === 0) return;
      // Drain the buffer atomically before awaiting the network call
      // — any events that arrive during the await go into a fresh
      // buffer and trigger their own flush.
      const drained = buffer;
      buffer = [];
      // Chunk by Firestore's per-batch cap so a single hydrate burst
      // splits cleanly across multiple commits.
      for (let i = 0; i < drained.length; i += BATCH_FLUSH_THRESHOLD) {
        const chunk = drained.slice(i, i + BATCH_FLUSH_THRESHOLD);
        const batch = writeBatch(firestore);
        for (const evt of chunk) {
          batch.set(doc(auditCol), evt);
        }
        try {
          await batch.commit();
        } catch (err) {
          onError(err);
        }
      }
    };

    const scheduleFlush = (): void => {
      if (timer !== null) return;
      timer = setTimeout(() => {
        timer = null;
        pendingFlush = pendingFlush
          .catch(() => undefined)
          .then(flushNow);
      }, debounceMs);
    };

    const handleEvent = (event: AuditEvent): void => {
      if (disposed) return;
      const persisted: PersistedAuditEvent = {
        type: event.type,
        timestamp: event.timestamp,
        fieldId: event.fieldId,
        actorUserId,
        timestampIso: new Date(event.timestamp).toISOString(),
      };
      if (event.instanceKey !== undefined) {
        persisted.instanceKey = event.instanceKey;
      }
      if (event.oldValue !== undefined) {
        persisted.oldValue = event.oldValue;
      }
      if (event.newValue !== undefined) {
        persisted.newValue = event.newValue;
      }

      // Coalesce same-field keystroke events.
      //
      // VA reported: "audit trail is capturing every character entry."
      // Reproduction: typing "Brandon" into a text field produces 7
      // change events (one per keystroke), each landing as its own
      // audit document in Firestore. Storage cost + audit-log noise
      // both grow linearly with character count.
      //
      // Collapse logic: if a buffered event for the SAME
      // (fieldId, instanceKey) is already pending in the same
      // debounce window, REPLACE that event's `newValue` with the
      // new event's `newValue` while preserving the buffered event's
      // ORIGINAL `oldValue`. Net effect: one audit event per field
      // edit per debounce window, capturing the value the field
      // started at and the value it ended at.
      //
      // Different fields (or different repeatable instances of the
      // same field) still emit their own events — coalescing only
      // applies to the exact same (fieldId, instanceKey) pair.
      const coalesceIdx = buffer.findIndex(
        b =>
          b.type === persisted.type &&
          b.fieldId === persisted.fieldId &&
          b.instanceKey === persisted.instanceKey,
      );
      if (coalesceIdx >= 0) {
        // Update newValue + timestamp; keep oldValue from the first
        // event in the window so the audit reads "started at X, ended at Y".
        const existing = buffer[coalesceIdx];
        if (persisted.newValue !== undefined) {
          existing.newValue = persisted.newValue;
        } else {
          delete existing.newValue;
        }
        existing.timestamp = persisted.timestamp;
        existing.timestampIso = persisted.timestampIso;
        scheduleFlush();
        return;
      }

      buffer.push(persisted);
      if (buffer.length >= BATCH_FLUSH_THRESHOLD) {
        // Flush immediately when the batch is full — don't wait for
        // the debounce timer.
        if (timer !== null) {
          clearTimeout(timer);
          timer = null;
        }
        pendingFlush = pendingFlush
          .catch(() => undefined)
          .then(flushNow);
      } else {
        scheduleFlush();
      }
    };

    const unsubscribe = source.subscribeAudit(handleEvent);

    const disconnect = async (): Promise<void> => {
      if (disposed) return;
      disposed = true;
      unsubscribe();
      // Run any in-flight flush, then drain whatever is left.
      await pendingFlush.catch(() => undefined);
      await flushNow();
    };

    const flush = async (): Promise<void> => {
      await pendingFlush.catch(() => undefined);
      await flushNow();
    };

    return {
      disconnect,
      flush,
      bufferedCount: () => buffer.length,
    };
  };

  return { connect };
};
